import { useState } from 'react';
import { useParentOverview } from '@/features/parent/use-parent-overview';
import { Button } from '@/components/ui/Button';
import { presentApiError } from '@/lib/form-errors';
import { useToggleShortlist } from './use-shortlist';

/**
 * "Send this to…" — a guardian putting a job on a teen's shortlist.
 * [Parent step 6, Teen step 6]
 *
 * The api has always accepted this; there was simply nowhere to press. With
 * one approved teen it is a single button, because making someone choose from
 * a list of one is a question with no information in it.
 */
export function ShareWithTeenButton({ jobId }: { jobId: string }) {
  const [error, setError] = useState<string | null>(null);
  const [sentTo, setSentTo] = useState<string[]>([]);

  const overview = useParentOverview();
  const share = useToggleShortlist();

  // Only teens whose guardian link is confirmed — an unverified link is not yet
  // a relationship this guardian can act through.
  const teens = (overview.data?.teens ?? []).filter((teen) => teen.verified);

  if (teens.length === 0) return null;

  async function send(teenId: string, name: string): Promise<void> {
    setError(null);
    try {
      await share.mutateAsync({ jobId, savedForTeenId: teenId, saved: false });
      setSentTo((current) => [...current, teenId]);
    } catch (caught) {
      setError(presentApiError(caught, `Could not send this to ${name}`).message);
    }
  }

  return (
    <div className="space-y-2">
      <p className="text-sm font-semibold text-ink">
        {teens.length === 1 ? 'Send this to your teen' : 'Send this to'}
      </p>

      <div className="flex flex-wrap gap-2">
        {teens.map((teen) => {
          const sent = sentTo.includes(teen.id);
          return (
            <Button
              key={teen.id}
              variant={sent ? 'ghost' : 'outline'}
              disabled={share.isPending || sent}
              onClick={() => void send(teen.id, teen.name)}
            >
              {sent ? `Sent to ${teen.name.split(' ')[0]}` : teen.name.split(' ')[0]}
            </Button>
          );
        })}
      </div>

      {error && <p className="text-xs font-medium text-red-600">{error}</p>}
      {sentTo.length > 0 && (
        <p className="text-xs text-slate-500">
          It is on their saved list now, and they have been told.
        </p>
      )}
    </div>
  );
}
